import { Api } from './generated/api'
import { toast } from '@/hooks/use-toast'
import type { AccountResponseDto, UpdateAccountDto } from '@/types/account'
import type { AuthResponseUserDto } from './generated/api'

// Access token used for attaching Authorization header on all API calls
let apiAccessToken: string | null = null
let authErrorHandler: (() => void) | null = null

export function registerAuthErrorHandler(handler: (() => void) | null) {
  authErrorHandler = handler
}

export function getApiUrl(): string {
  // Primary source: VITE_API_URL
  const fromEnv = import.meta.env.VITE_API_URL
  if (fromEnv && fromEnv.trim()) {
    return fromEnv.trim()
  }

  const inferred = inferApiUrlFromWindow()
  if (inferred) {
    return inferred
  }

  // Sensible local default for developers if env not set
  return 'http://localhost:3000'
}

// Maps the page's hostname to the API's base URL. Production and the dev
// domain name a different host or port. A page served by the Vite dev server
// on localhost, 127.0.0.1 (the iOS simulator's WebView and a desktop browser)
// or 10.0.2.2 (the Android emulator's WebView) gets its own origin back, so its
// /api calls reach the Vite proxy, which forwards them to the API
// (`server.proxy` in vite.config.ts).
export function inferApiUrlFromWindow(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const hostname = window.location.hostname
  if (!hostname) {
    return null
  }

  if (hostname === 'white-gi.onrender.com') {
    return 'https://white-gi-api.onrender.com'
  }

  if (hostname === 'dev.white-gi.com') {
    return 'https://dev.white-gi.com:443'
  }

  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '10.0.2.2'
  ) {
    return window.location.origin
  }

  return null
}

/**
 * Get or create API client with dynamic baseUrl
 * This ensures the baseUrl is always current based on window.location
 */
export function getApiClient() {
  const currentBaseUrl = getApiUrl()

  // Update the existing client's baseUrl if it has changed
  if (apiClient.baseUrl !== currentBaseUrl) {
    apiClient.baseUrl = currentBaseUrl
  }

  return apiClient
}

// Create the API client instance with error handling
export const apiClient = new Api({
  baseUrl: getApiUrl(),
  baseApiParams: {
    headers: {
      'Content-Type': 'application/json',
    },
  },
  securityWorker: token =>
    token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : undefined,
  customFetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    const isRequestInstance = () =>
      typeof Request !== 'undefined' && input instanceof Request

    const cloneRequestInfo = () =>
      isRequestInstance() ? (input as Request).clone() : input

    let authErrorNotified = false

    const notifyAuthError = () => {
      if (authErrorNotified) {
        return
      }
      authErrorNotified = true
      if (authErrorHandler) {
        try {
          authErrorHandler()
        } catch {
          // Ignore handler errors
        }
      }
    }

    const performRequest = async (
      requestInit: RequestInit | undefined
    ): Promise<Response> => {
      const response = await fetch(cloneRequestInfo(), requestInit)

      if (response.ok) {
        return response
      }

      const unauthorized = response.status === 401

      const errorData = await response
        .json()
        .catch(() => ({ message: 'An error occurred' }))
      const errorMessage =
        errorData.message || `HTTP ${response.status}: ${response.statusText}`

      if (unauthorized) {
        notifyAuthError()
      } else {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }

      throw new Error(errorMessage)
    }

    try {
      return await performRequest(init)
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        toast({
          title: 'Network Error',
          description:
            'Unable to connect to the server. Please check your internet connection.',
          variant: 'destructive',
        })
      }

      throw error
    }
  },
})

// Keep the shared API client's access token in sync with AuthContext
export function setApiAccessToken(token: string | null) {
  apiAccessToken = token
  apiClient.setSecurityData(token)
}

// Used by embed pages that receive a Bearer token via window injection
// rather than via the standard auth context flow.
export function initApiClientWithToken(token: string) {
  apiClient.setSecurityData(token)
}

function withAuthHeaders(headers: Record<string, string> = {}) {
  if (!apiAccessToken) {
    return headers
  }

  return {
    ...headers,
    Authorization: `Bearer ${apiAccessToken}`,
  }
}

// Helper functions for common operations
export interface TestLoginResponse {
  accessToken: string
  user: AuthResponseUserDto
}

export const authAPI = {
  // OAuth login redirects
  googleLogin: () => {
    window.location.href = `${getApiUrl()}/api/auth/google`
  },

  appleLogin: () => {
    window.location.href = `${getApiUrl()}/api/auth/apple`
  },

  // Get current user profile (requires Authorization header upstream)
  getProfile: () => getApiClient().api.authControllerGetProfile(),

  // Test login (development only)
  testLogin: async (email: string): Promise<TestLoginResponse> => {
    const apiUrl = getApiUrl()

    const response = await fetch(`${apiUrl}/api/auth/dev-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: 'Test login failed' }))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  },
}

export const healthAPI = {
  // Health check
  checkHealth: () => getApiClient().api.healthControllerGetHealth(),
}

export const accountAPI = {
  // Get authenticated user's account information
  getAccount: async (): Promise<AccountResponseDto> => {
    const response = await fetch(`${getApiUrl()}/api/account`, {
      method: 'GET',
      headers: withAuthHeaders({
        'Content-Type': 'application/json',
      }),
    })

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: 'Failed to fetch account' }))
      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  },

  // Update authenticated user's account information
  updateAccount: async (
    data: UpdateAccountDto
  ): Promise<AccountResponseDto> => {
    const response = await fetch(`${getApiUrl()}/api/account`, {
      method: 'PUT',
      headers: withAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: 'Failed to update account' }))

      // Handle validation errors
      if (response.status === 400) {
        const message = Array.isArray(errorData.message)
          ? errorData.message.join(', ')
          : errorData.message
        throw new Error(message || 'Validation error')
      }

      throw new Error(errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  },
}

export default apiClient
